import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { TokenTypeEnum, DayOfWeekEnum } from '../../types/booking';
import { ICoachServicePackage } from '../coach/coachSlice';
import { ICoachAvailableTimes } from '../miniweb/miniwebSlice';
import { resetState, logout } from '../GlobalAction';

export enum BookingTypeEnum {
  IN_PERSON = 'IN_PERSON',
  ONLINE = 'ONLINE',
}

export enum VideoCallPlatform {
  GOOGLE_MEET = 'GOOGLE_MEET',
  ZOOM = 'ZOOM',
}

export interface ICoachOfficeDetails {
  coachId: string;
  officeAddress: string;
  officeCountry?: string;
  officePhone?: string;
  makeAddressPublic?: boolean;
  makePhonePublic?: boolean;
  activeStep: number;
  isProvideInPersonConsultations: boolean;
  isProvideOnlineConsultations?: boolean | null;
  isGeneralSettings?: boolean | null;
}
export interface ICoachUpdateBookingData {
  bookingId: number;
  servicePackageId: string;
  date: Date;
}
export interface IGetBookings {
  date: number;
  month: number;
  year: number;
}
export interface IGetBookingsForSelectedMonth {
  month: number;
  year: number;
}

export interface IGetCoachConfigurationResponse {
  coachId: string;
  isProvideOnlineConsultations: boolean;
  isProvideInPersonConsultations: boolean;
  videoCallPlatform?: VideoCallPlatform | null;
  activeStep: number;
  calendar: TokenTypeEnum | null;
  makePhonePublic?: boolean;
  makeAddressPublic?: boolean;
  hasBookings?: boolean | null;
  calendarUsername: string | null;
  videoCallPlatformUsername: string | null;
  url: string | null;
  officeAddress?: string;
  officePhone?: string;
}

export interface IConsultationPreferenceInput {
  coachId: string;
  isProvideOnlineConsultations?: boolean;
  isProvideInPersonConsultations?: boolean;
  videoCallPlatform?: VideoCallPlatform;
  activeStep: number;
  videoCallPlatformUsername?: string;
}

export enum BookingStatus {
  CONFIRMED = 'CONFIRMED',
  CANCELED = 'CANCELED',
}

export interface IBooking {
  id?: number;
  coachId: string;
  duration: number;
  meetingLink?: string;
  clientFirstName: string;
  clientLastName: string;
  clientEmail: string;
  clientPhone: string;
  servicePackage: ICoachServicePackage;
  servicePackageId: string;
  isAgreedToTerms: boolean;
  isSubscribedToNewsletter: boolean;
  bookingStatus: BookingStatus;
  date: Date;
  referenceText?: string;
  referenceNote?: string;
}

export interface Dates {
  date: Date;
}

export interface IAvailability {
  startTime: number;
  endTime: number;
  dayOfWeek: DayOfWeekEnum;
}

export interface ICoachAvailability {
  coachId: string;
  availabilities: IAvailability[];
  activeStep?: number;
}

export interface ISaveCalendar {
  coachId: string;
  calendar: TokenTypeEnum;
  activeStep?: number;
  calendarUsername: string | null;
}
export interface ISaveVideoPlatform {
  coachId: string;
  videoCallPlatform: VideoCallPlatform;
  activeStep?: number;
  videoCallPlatformUsername: string | null;
}

export interface ICoachBookingPageUrl {
  coachId: string;
  url: string;
  activeStep: number;
}

export interface ICheckUrlExistenceResponse {
  checkUrlExistence: {
    success: boolean;
  };
}

export interface ICalendarData {
  coachId: string;
  calendar: TokenTypeEnum | null;
  calendarUsername: string | null;
  activeStep?: number;
}

export interface IVideoPlatformData {
  coachId: string;
  videoCallPlatform: VideoCallPlatform | null | undefined;
  videoCallPlatformUsername: string | null;
  activeStep?: number;
}

interface IBookingState {
  bookings: IBooking[];
  fetchBookingsLoading: boolean;
  selectedDate: string;
  highlightedDays: number[];
  fetchBookingsByMonthLoading: boolean;
  countryCode: string;
  createCoachAvailabilityLoading: boolean;
  createCoachAvailabilitySuccess: boolean;
  updateCoachAvailabilityLoading: boolean;
  updateCoachAvailabilitySuccess: boolean;
  googleCalendarAuthURILoading: boolean;
  googleCalendarAuthURISuccess: boolean;
  googleCalendarAuthURI: string | null;
  googleMeetAuthURILoading: boolean;
  googleMeetAuthURIFailure: boolean;
  googleMeetAuthURISuccess: boolean;
  googleCalendarAuthURIFailure: boolean;
  googleMeetAuthURI: string | null;
  outlookCalendarAuthURILoading: boolean;
  outlookCalendarAuthURISuccess: boolean;
  outlookCalendarAuthURIFailure: boolean;
  outlookCalendarAuthURI: string | null;
  linkedCalendarLoading: boolean;
  linkedCalendarSuccess: boolean;
  linkedVideoCallPlatformLoading: boolean;
  linkedVideoCallPlatformSuccess: boolean;
  checkTokenExistsLoading: boolean;
  checkTokenExistsFailure: boolean;
  checkVideoPlatformTokenExistsLoading: boolean;
  checkVideoPlatformTokenExistsFailure: boolean;
  isTokenExists: boolean;
  isVideoPlatformTokenExists: boolean;
  saveCoachOfficeDetailsLoading: boolean;
  createConsultationPreferenceLoading: boolean;
  createConsultationPreferenceSuccess: boolean;
  coachConfigurationsLoading: boolean;
  coachConfigurations: IGetCoachConfigurationResponse;
  saveOfficedetailsLoading: boolean;
  saveOfficedetailsSuccess: boolean;
  saveBookingPageUrlLoading: boolean;
  saveBookingPageUrlSuccess: boolean;
  fetchCoachAvailabilitiesLoading: boolean;
  fetchCoachAvailabilitiesSuccess: boolean;
  deleteCalendarLoading: boolean;
  deleteCalendarSuccess: boolean;
  deleteVideoPlatformLoading: boolean;
  deleteVideoPlatformSuccess: boolean;
  coachAvailabilities?: IAvailability[];
  zoomAuthURILoading: boolean;
  zoomAuthURISuccess: boolean;
  zoomAuthURIFailure: boolean;
  zoomAuthURI: string | null;
  username: string | null;
  videoPlatformUsername: string | null;
  urlExistStatus: boolean | null;
  urlExistError?: string | null;
  cancelBookingLoading: boolean;
  cancelBookingSuccess: boolean;
  rescheduleTimes: Date[];
  getRescheduleTimesLoading: boolean;
  updateBookingLoading: boolean;
  updateBookingSuccess: boolean;
  error?: string | null;
  updateCoachActiveStepLoading: boolean;
  updateCoachActiveStepSuccess: boolean;
}

const initialState: IBookingState = {
  bookings: [],
  fetchBookingsLoading: false,
  fetchBookingsByMonthLoading: false,
  selectedDate: dayjs().toISOString(),
  highlightedDays: [],
  countryCode: '',
  createCoachAvailabilityLoading: false,
  createCoachAvailabilitySuccess: false,
  updateCoachAvailabilityLoading: false,
  updateCoachAvailabilitySuccess: false,
  googleCalendarAuthURILoading: false,
  googleCalendarAuthURISuccess: false,
  googleCalendarAuthURI: null,
  outlookCalendarAuthURILoading: false,
  outlookCalendarAuthURISuccess: false,
  outlookCalendarAuthURI: null,
  linkedCalendarLoading: false,
  linkedCalendarSuccess: false,
  checkTokenExistsLoading: false,
  isTokenExists: false,
  saveCoachOfficeDetailsLoading: false,
  createConsultationPreferenceLoading: false,
  createConsultationPreferenceSuccess: false,
  coachConfigurationsLoading: true,
  coachConfigurations: {
    activeStep: 0,
    coachId: '',
    isProvideOnlineConsultations: false,
    calendar: null,
    isProvideInPersonConsultations: false,
    calendarUsername: null,
    videoCallPlatformUsername: null,
    url: null,
  },
  saveOfficedetailsLoading: false,
  saveOfficedetailsSuccess: false,
  saveBookingPageUrlLoading: false,
  saveBookingPageUrlSuccess: false,
  fetchCoachAvailabilitiesLoading: false,
  fetchCoachAvailabilitiesSuccess: false,
  deleteCalendarLoading: false,
  deleteCalendarSuccess: false,
  zoomAuthURILoading: false,
  zoomAuthURISuccess: false,
  zoomAuthURI: null,
  username: null,
  urlExistStatus: null,
  videoPlatformUsername: null,
  checkVideoPlatformTokenExistsLoading: false,
  googleMeetAuthURILoading: false,
  googleMeetAuthURISuccess: false,
  googleMeetAuthURI: null,
  isVideoPlatformTokenExists: false,
  deleteVideoPlatformLoading: false,
  deleteVideoPlatformSuccess: false,
  linkedVideoCallPlatformLoading: false,
  linkedVideoCallPlatformSuccess: false,
  cancelBookingLoading: false,
  cancelBookingSuccess: false,
  getRescheduleTimesLoading: false,
  rescheduleTimes: [],
  updateBookingLoading: false,
  updateBookingSuccess: false,
  googleMeetAuthURIFailure: false,
  googleCalendarAuthURIFailure: false,
  zoomAuthURIFailure: false,
  outlookCalendarAuthURIFailure: false,
  checkTokenExistsFailure: false,
  checkVideoPlatformTokenExistsFailure: false,
  updateCoachActiveStepLoading: false,
  updateCoachActiveStepSuccess: false,
  coachAvailabilities: [],
};

const bookingSlice = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    fetchBookings(state: IBookingState, _action: PayloadAction<IGetBookings>) {
      state.fetchBookingsLoading = true;
      state.bookings = [];
    },
    fetchBookingsSuccess(state: IBookingState, action: PayloadAction<IBooking[]>) {
      state.bookings = action.payload;
      state.fetchBookingsLoading = false;
    },
    fetchBookingsFailure(state: IBookingState) {
      state.fetchBookingsLoading = false;
    },
    selectDate(state: IBookingState, action: PayloadAction<{ date: string }>) {
      state.selectedDate = action.payload.date;
    },
    fetchBookingsByMonth(
      state: IBookingState,
      _action: PayloadAction<IGetBookingsForSelectedMonth>
    ) {
      state.highlightedDays = [];
      state.fetchBookingsByMonthLoading = true;
    },
    fetchBookingsByMonthSuccess(state: IBookingState, action: PayloadAction<Dates[]>) {
      state.highlightedDays = action.payload.map((date) => dayjs(date.date).date());
      state.fetchBookingsByMonthLoading = false;
    },
    fetchBookingsByMonthFailure(state: IBookingState) {
      state.fetchBookingsByMonthLoading = false;
    },
    fetchCountryCode(_state: IBookingState) {},
    fetchCountryCodeSuccess(state: IBookingState, action: PayloadAction<string>) {
      state.countryCode = action.payload;
    },
    fetchCountryCodeFailure(_state: IBookingState) {},
    createCoachAvailability(state, _action: PayloadAction<ICoachAvailability>) {
      state.createCoachAvailabilityLoading = true;
    },
    createCoachAvailabilitySuccess(state, action: PayloadAction<ICoachAvailability>) {
      state.coachConfigurations.activeStep = 2;
      state.coachAvailabilities = action.payload.availabilities;
      state.createCoachAvailabilityLoading = false;
      state.createCoachAvailabilitySuccess = true;
    },
    createCoachAvailabilityFailure(state: IBookingState) {
      state.createCoachAvailabilityLoading = false;
      state.createCoachAvailabilitySuccess = false;
    },
    updateCoachAvailability(state, _action: PayloadAction<ICoachAvailability>) {
      state.updateCoachAvailabilityLoading = true;
      state.updateCoachAvailabilitySuccess = false;
    },
    updateCoachAvailabilitySuccess(state, action: PayloadAction<ICoachAvailability>) {
      state.coachAvailabilities = action.payload.availabilities;
      state.updateCoachAvailabilityLoading = false;
      state.updateCoachAvailabilitySuccess = true;
    },
    updateCoachAvailabilityFailure(state) {
      state.updateCoachAvailabilityLoading = false;
      state.updateCoachAvailabilitySuccess = false;
    },
    resetCreateCoachAvailability(state: IBookingState) {
      state.createCoachAvailabilitySuccess = false;
    },
    getGoogleCalendarAuthURI(state: IBookingState, _action: PayloadAction<IVideoPlatformData>) {
      state.googleCalendarAuthURILoading = true;
      state.googleCalendarAuthURISuccess = false;
      state.googleCalendarAuthURIFailure = false;
    },
    getGoogleCalendarAuthURISuccess(state: IBookingState, action: PayloadAction<string>) {
      state.googleCalendarAuthURILoading = false;
      state.googleCalendarAuthURI = action.payload;
      state.googleCalendarAuthURISuccess = true;
    },
    getGoogleCalendarAuthURIFailure(state: IBookingState) {
      state.googleCalendarAuthURILoading = false;
      state.googleCalendarAuthURISuccess = false;
      state.googleCalendarAuthURIFailure = true;
    },
    getGoogleMeetAuthURI(state: IBookingState, _action: PayloadAction<ICalendarData>) {
      state.googleMeetAuthURILoading = true;
      state.googleMeetAuthURISuccess = false;
      state.googleMeetAuthURIFailure = false;
    },
    getGoogleMeetAuthURISuccess(state: IBookingState, action: PayloadAction<string>) {
      state.googleMeetAuthURILoading = false;
      state.googleMeetAuthURI = action.payload;
      state.googleMeetAuthURISuccess = true;
    },
    getGoogleMeetAuthURIFailure(state: IBookingState) {
      state.googleMeetAuthURILoading = false;
      state.googleMeetAuthURISuccess = false;
      state.googleMeetAuthURIFailure = true;
    },
    getOutlookCalendarAuthURI(state: IBookingState) {
      state.outlookCalendarAuthURILoading = true;
      state.outlookCalendarAuthURISuccess = false;
      state.outlookCalendarAuthURIFailure = false;
    },
    getOutlookCalendarAuthURISuccess(state: IBookingState, action: PayloadAction<string>) {
      state.outlookCalendarAuthURILoading = false;
      state.outlookCalendarAuthURI = action.payload;
      state.outlookCalendarAuthURISuccess = true;
    },
    getOutlookCalendarAuthURIFailure(state: IBookingState) {
      state.outlookCalendarAuthURILoading = false;
      state.outlookCalendarAuthURISuccess = false;
      state.outlookCalendarAuthURIFailure = true;
    },
    saveLinkedCalendar(state: IBookingState, _action: PayloadAction<ISaveCalendar>) {
      state.linkedCalendarLoading = true;
      state.linkedCalendarSuccess = false;
    },
    saveLinkedCalendarSuccess(
      state: IBookingState,
      action: PayloadAction<IGetCoachConfigurationResponse>
    ) {
      state.coachConfigurations.calendar = action.payload.calendar;
      state.coachConfigurations.calendarUsername = action.payload.calendarUsername;
      state.linkedCalendarLoading = false;
      state.linkedCalendarSuccess = true;
    },
    saveLinkedCalendarFailure(state: IBookingState) {
      state.linkedCalendarLoading = false;
      state.linkedCalendarSuccess = false;
    },
    resetSaveLinkedCalendar(state: IBookingState) {
      state.linkedCalendarSuccess = false;
      state.googleCalendarAuthURISuccess = false;
      state.outlookCalendarAuthURISuccess = false;
    },
    saveLinkedVideoPlatform(state: IBookingState, _action: PayloadAction<ISaveVideoPlatform>) {
      state.linkedVideoCallPlatformLoading = true;
      state.linkedVideoCallPlatformSuccess = false;
    },
    saveLinkedVideoPlatformSuccess(
      state: IBookingState,
      action: PayloadAction<IGetCoachConfigurationResponse>
    ) {
      state.coachConfigurations.videoCallPlatform = action.payload.videoCallPlatform;
      state.coachConfigurations.videoCallPlatformUsername =
        action.payload.videoCallPlatformUsername;
      state.linkedVideoCallPlatformLoading = false;
      state.linkedVideoCallPlatformSuccess = true;
    },
    saveLinkedVideoCallPlatformFailure(state: IBookingState) {
      state.linkedVideoCallPlatformLoading = false;
      state.linkedVideoCallPlatformSuccess = false;
    },
    resetSaveLinkedVideoCallPlatform(state: IBookingState) {
      state.linkedVideoCallPlatformSuccess = false;
      state.googleMeetAuthURISuccess = false;
      state.zoomAuthURISuccess = false;
    },
    checkTokenExists(
      state: IBookingState,
      _action: PayloadAction<{ coachId: string; tokenType: TokenTypeEnum }>
    ) {
      state.checkTokenExistsLoading = true;
      state.isTokenExists = false;
      state.checkTokenExistsFailure = false;
    },
    checkTokenExistsSuccess(
      state: IBookingState,
      action: PayloadAction<{ isTokenExist: boolean; userName?: string }>
    ) {
      state.checkTokenExistsLoading = false;
      state.isTokenExists = action.payload.isTokenExist;
      if (action.payload.userName) {
        state.username = action.payload.userName;
      }
    },
    checkTokenExistsFailure(state: IBookingState) {
      state.checkTokenExistsLoading = false;
      state.isTokenExists = false;
      state.username = null;
      state.checkTokenExistsFailure = true;
    },
    resetCheckTokenExists(state: IBookingState) {
      state.isTokenExists = false;
    },
    resetCalendarAuthURI(state: IBookingState) {
      state.googleCalendarAuthURI = null;
      state.outlookCalendarAuthURI = null;
    },
    checkVideoPlatformTokenExists(
      state: IBookingState,
      _action: PayloadAction<{ coachId: string; tokenType: TokenTypeEnum }>
    ) {
      state.checkVideoPlatformTokenExistsLoading = true;
      state.isVideoPlatformTokenExists = false;
      state.checkVideoPlatformTokenExistsFailure = false;
    },
    checkVideoPlatformTokenExistsSuccess(
      state: IBookingState,
      action: PayloadAction<{ isTokenExist: boolean; userName?: string }>
    ) {
      state.checkVideoPlatformTokenExistsLoading = false;
      state.isVideoPlatformTokenExists = action.payload.isTokenExist;
      if (action.payload.userName) {
        state.videoPlatformUsername = action.payload.userName;
      }
    },
    checkVideoPlatformTokenExistsFailure(state: IBookingState) {
      state.checkVideoPlatformTokenExistsLoading = false;
      state.isVideoPlatformTokenExists = false;
      state.videoPlatformUsername = null;
      state.checkVideoPlatformTokenExistsFailure = true;
    },
    resetCheckVideoPlatformTokenExists(state: IBookingState) {
      state.isVideoPlatformTokenExists = false;
    },
    resetVideoPlatforAuthURI(state: IBookingState) {
      state.googleMeetAuthURI = null;
      state.zoomAuthURI = null;
    },
    fetchCoachConfigurations: (
      state: IBookingState,
      _action: PayloadAction<{ coachId: string }>
    ) => {
      state.coachConfigurationsLoading = true;
    },
    fetchCoachConfigurationsSuccess: (
      state: IBookingState,
      action: PayloadAction<IGetCoachConfigurationResponse>
    ) => {
      state.coachConfigurationsLoading = false;
      state.coachConfigurations = action.payload;
    },
    fetchCoachConfigurationsFailure: (state: IBookingState) => {
      state.coachConfigurationsLoading = false;
    },
    createConsultationPreference(
      state: IBookingState,
      _action: PayloadAction<IConsultationPreferenceInput>
    ) {
      state.createConsultationPreferenceLoading = true;
    },
    createConsultationPreferenceSuccess(
      state: IBookingState,
      action: PayloadAction<IGetCoachConfigurationResponse>
    ) {
      state.coachConfigurations = action.payload;
      state.createConsultationPreferenceLoading = false;
      state.createConsultationPreferenceSuccess = true;
    },
    createConsultationPreferenceFailure(state: IBookingState) {
      state.createConsultationPreferenceLoading = false;
      state.createConsultationPreferenceSuccess = false;
    },
    resetCreateConsultationPreference(state: IBookingState) {
      state.createConsultationPreferenceSuccess = false;
    },
    saveOfficedetails(state: IBookingState, _action: PayloadAction<ICoachOfficeDetails>) {
      state.saveOfficedetailsLoading = true;
    },
    saveOfficedetailsSuccess(state: IBookingState, action: PayloadAction<ICoachOfficeDetails>) {
      state.coachConfigurations.isProvideInPersonConsultations =
        action.payload.isProvideInPersonConsultations;
      state.coachConfigurations.activeStep = 4;
      state.saveOfficedetailsLoading = false;
      state.saveOfficedetailsSuccess = true;
    },
    saveGeneralSettings(state: IBookingState, action: PayloadAction<ICoachOfficeDetails>) {
      state.saveOfficedetailsLoading = false;
      state.saveOfficedetailsSuccess = true;
      state.coachConfigurations.isProvideInPersonConsultations =
        action.payload.isProvideInPersonConsultations;
      state.coachConfigurations.isProvideOnlineConsultations =
        !!action.payload.isProvideOnlineConsultations;
      state.coachConfigurations.makeAddressPublic = action.payload.makeAddressPublic;
      state.coachConfigurations.makePhonePublic = action.payload.makePhonePublic;
      state.coachConfigurations.officeAddress = action.payload.officeAddress;
      state.coachConfigurations.officePhone = action.payload.officePhone;
    },
    updateCoachInpersonConsultationPreference(
      state: IBookingState,
      action: PayloadAction<boolean>
    ) {
      state.coachConfigurations.isProvideInPersonConsultations = action.payload;
    },
    saveOfficedetailsFailure(state: IBookingState) {
      state.saveOfficedetailsLoading = false;
      state.saveOfficedetailsSuccess = false;
    },
    resetSaveOfficedetails(state: IBookingState) {
      state.saveOfficedetailsSuccess = false;
    },
    saveBookingPageUrl(state: IBookingState, _action: PayloadAction<ICoachBookingPageUrl>) {
      state.saveBookingPageUrlLoading = true;
    },
    saveBookingPageUrlSuccess(state: IBookingState, action: PayloadAction<string>) {
      state.coachConfigurations.activeStep = -1;
      state.saveBookingPageUrlLoading = false;
      state.saveBookingPageUrlSuccess = true;
      state.coachConfigurations.url = action.payload;
    },
    saveBookingPageUrlFailure(state: IBookingState) {
      state.saveBookingPageUrlLoading = false;
      state.saveBookingPageUrlSuccess = false;
    },
    resetSaveBookingPageUrl(state: IBookingState) {
      state.saveBookingPageUrlSuccess = false;
    },
    fetchCoachAvailabilities(state: IBookingState, _action: PayloadAction<{ coachId: string }>) {
      state.coachAvailabilities = [];
      state.fetchCoachAvailabilitiesLoading = true;
    },
    fetchCoachAvailabilitiesSuccess(state: IBookingState, action: PayloadAction<IAvailability[]>) {
      state.coachAvailabilities = action.payload;
      state.fetchCoachAvailabilitiesLoading = false;
      state.fetchCoachAvailabilitiesSuccess = true;
    },
    fetchCoachAvailabilitiesFailure(state: IBookingState) {
      state.fetchCoachAvailabilitiesLoading = false;
      state.fetchCoachAvailabilitiesSuccess = false;
    },
    getRescheduleTimesRequest: (
      state: IBookingState,
      _action: PayloadAction<{
        bookingId: number;
        date: number;
        month: number;
        year: number;
      }>
    ) => {
      state.getRescheduleTimesLoading = true;
    },
    getRescheduleTimesRequestSuccess: (
      state: IBookingState,
      action: PayloadAction<ICoachAvailableTimes[]>
    ) => {
      state.getRescheduleTimesLoading = false;
      state.rescheduleTimes = action.payload?.map((i) => i.date);
    },
    getRescheduleTimesRequestFailure: (state: IBookingState, action: PayloadAction<string>) => {
      state.getRescheduleTimesLoading = false;
      state.error = action.payload;
      state.rescheduleTimes = [];
    },
    resetFetchCoachAvailabilities(state: IBookingState) {
      state.fetchCoachAvailabilitiesSuccess = false;
    },
    deleteCalendar(
      state: IBookingState,
      _action: PayloadAction<{ calenderType: TokenTypeEnum; isConfigured: boolean }>
    ) {
      state.deleteCalendarLoading = true;
      state.isTokenExists = false;
      state.username = null;
      state.deleteCalendarSuccess = false;
    },
    deleteCalendarSuccess(state: IBookingState) {
      state.coachConfigurations.calendar = null;
      state.deleteCalendarLoading = false;
      state.deleteCalendarSuccess = true;
    },
    deleteCalendarFailure(state: IBookingState) {
      state.deleteCalendarLoading = false;
      state.deleteCalendarSuccess = false;
    },
    resetDeleteCalendar(state: IBookingState) {
      state.deleteCalendarSuccess = false;
    },
    deleteVideoPlatform(
      state: IBookingState,
      _action: PayloadAction<{ calenderType: TokenTypeEnum; isConfigured: boolean }>
    ) {
      state.deleteVideoPlatformLoading = true;
      state.isVideoPlatformTokenExists = false;
      state.videoPlatformUsername = null;
      state.deleteVideoPlatformSuccess = false;
    },
    deleteVideoPlatformSuccess(state: IBookingState) {
      state.coachConfigurations.videoCallPlatform = null;
      state.deleteVideoPlatformLoading = false;
      state.deleteVideoPlatformSuccess = true;
    },
    deleteVideoPlatformFailure(state: IBookingState) {
      state.deleteVideoPlatformLoading = false;
      state.deleteVideoPlatformSuccess = false;
    },
    resetDeleteVideoPlatform(state: IBookingState) {
      state.deleteVideoPlatformSuccess = false;
    },
    getZoomAuthURI(state: IBookingState) {
      state.zoomAuthURILoading = true;
      state.zoomAuthURISuccess = false;
      state.zoomAuthURIFailure = false;
    },
    getZoomAuthURISuccess(state: IBookingState, action: PayloadAction<string>) {
      state.zoomAuthURILoading = false;
      state.zoomAuthURISuccess = true;
      state.zoomAuthURI = action.payload;
    },
    getZoomAuthURIFailure(state: IBookingState) {
      state.zoomAuthURILoading = false;
      state.zoomAuthURISuccess = false;
      state.zoomAuthURIFailure = true;
    },
    checkUrlExistence(state: IBookingState, _action: PayloadAction<string>) {
      state.urlExistStatus = null;
    },
    checkUrlExistenceSuccess(state: IBookingState, action: PayloadAction<boolean>) {
      state.urlExistStatus = action.payload;
    },
    checkUrlExistenceFailure(state: IBookingState, action: PayloadAction<string | undefined>) {
      state.urlExistStatus = action.payload ? false : null;
      state.urlExistError = action.payload;
    },
    resetCheckUrlExistence(state: IBookingState) {
      state.urlExistStatus = null;
      state.urlExistError = null;
    },
    resetCoachConfigurations(state: IBookingState) {
      state.coachConfigurations = {
        activeStep: 0,
        coachId: '',
        isProvideOnlineConsultations: false,
        calendar: null,
        isProvideInPersonConsultations: false,
        calendarUsername: null,
        videoCallPlatformUsername: null,
        url: null,
      };
    },
    cancelBooking(state: IBookingState, _action: PayloadAction<IBooking>) {
      state.cancelBookingLoading = true;
      state.cancelBookingSuccess = false;
    },
    cancelBookingSuccess(state: IBookingState, action: PayloadAction<IBooking>) {
      state.cancelBookingLoading = false;
      state.cancelBookingSuccess = true;
      const booking = state.bookings.find((b) => b.id === action.payload.id);
      if (booking) {
        booking.bookingStatus = action.payload.bookingStatus;
      }
    },
    cancelBookingFailure(state: IBookingState) {
      state.cancelBookingLoading = false;
      state.cancelBookingSuccess = false;
    },
    updateBooking: (state, _action: PayloadAction<ICoachUpdateBookingData>) => {
      state.updateBookingLoading = true;
      state.updateBookingSuccess = false;
    },
    updateBookingSuccess: (state, action: PayloadAction<IBooking>) => {
      state.updateBookingLoading = false;
      state.updateBookingSuccess = true;
      state.bookings = state.bookings.filter((booking) => booking.id !== action.payload.id);
      state.bookings.push(action.payload);
    },
    updateBookingFailure: (state) => {
      state.updateBookingLoading = false;
      state.updateBookingSuccess = false;
    },
    updateCoachActiveStep: (state, _action: PayloadAction<{ activeStep: number }>) => {
      state.updateCoachActiveStepLoading = true;
      state.updateCoachActiveStepSuccess = false;
    },
    updateCoachActiveStepSuccess: (state, action: PayloadAction<number>) => {
      state.coachConfigurations.activeStep = action.payload;
      state.updateCoachActiveStepLoading = false;
      state.updateCoachActiveStepSuccess = true;
    },
    updateCoachActiveStepFailure: (state) => {
      state.coachConfigurations.activeStep = 0;
      state.updateCoachActiveStepLoading = false;
      state.updateCoachActiveStepSuccess = false;
    },
    resetUpdateActiveStep(state: IBookingState) {
      state.updateCoachActiveStepSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetState, () => initialState);
    builder.addCase(logout, () => initialState);
  },
});

export const {
  fetchBookings,
  fetchBookingsSuccess,
  fetchBookingsFailure,
  selectDate,
  fetchBookingsByMonth,
  fetchBookingsByMonthSuccess,
  fetchBookingsByMonthFailure,
  fetchCountryCode,
  fetchCountryCodeSuccess,
  fetchCountryCodeFailure,
  createCoachAvailability,
  createCoachAvailabilitySuccess,
  createCoachAvailabilityFailure,
  updateCoachAvailability,
  updateCoachAvailabilitySuccess,
  updateCoachAvailabilityFailure,
  getGoogleCalendarAuthURI,
  getGoogleCalendarAuthURISuccess,
  getGoogleCalendarAuthURIFailure,
  getOutlookCalendarAuthURI,
  getOutlookCalendarAuthURISuccess,
  getOutlookCalendarAuthURIFailure,
  saveLinkedCalendar,
  saveLinkedCalendarSuccess,
  saveLinkedCalendarFailure,
  resetCreateCoachAvailability,
  resetSaveLinkedCalendar,
  checkTokenExists,
  checkTokenExistsSuccess,
  checkTokenExistsFailure,
  resetCheckTokenExists,
  resetCalendarAuthURI,
  createConsultationPreference,
  createConsultationPreferenceSuccess,
  createConsultationPreferenceFailure,
  fetchCoachConfigurations,
  fetchCoachConfigurationsSuccess,
  fetchCoachConfigurationsFailure,
  saveOfficedetails,
  saveOfficedetailsSuccess,
  saveOfficedetailsFailure,
  saveBookingPageUrl,
  saveBookingPageUrlSuccess,
  saveBookingPageUrlFailure,
  resetSaveBookingPageUrl,
  resetCreateConsultationPreference,
  resetSaveOfficedetails,
  fetchCoachAvailabilities,
  fetchCoachAvailabilitiesSuccess,
  fetchCoachAvailabilitiesFailure,
  resetFetchCoachAvailabilities,
  deleteCalendar,
  deleteCalendarSuccess,
  deleteCalendarFailure,
  getZoomAuthURI,
  getZoomAuthURISuccess,
  getZoomAuthURIFailure,
  resetDeleteCalendar,
  checkUrlExistence,
  checkUrlExistenceSuccess,
  checkUrlExistenceFailure,
  resetCheckUrlExistence,
  resetCoachConfigurations,
  getGoogleMeetAuthURI,
  getGoogleMeetAuthURISuccess,
  getGoogleMeetAuthURIFailure,
  checkVideoPlatformTokenExists,
  checkVideoPlatformTokenExistsSuccess,
  checkVideoPlatformTokenExistsFailure,
  resetCheckVideoPlatformTokenExists,
  resetVideoPlatforAuthURI,
  deleteVideoPlatform,
  deleteVideoPlatformSuccess,
  deleteVideoPlatformFailure,
  resetDeleteVideoPlatform,
  saveLinkedVideoCallPlatformFailure,
  saveLinkedVideoPlatform,
  saveLinkedVideoPlatformSuccess,
  resetSaveLinkedVideoCallPlatform,
  cancelBooking,
  cancelBookingFailure,
  cancelBookingSuccess,
  getRescheduleTimesRequest,
  getRescheduleTimesRequestSuccess,
  getRescheduleTimesRequestFailure,
  updateBooking,
  updateBookingFailure,
  updateBookingSuccess,
  updateCoachInpersonConsultationPreference,
  updateCoachActiveStep,
  updateCoachActiveStepSuccess,
  updateCoachActiveStepFailure,
  resetUpdateActiveStep,
  saveGeneralSettings,
} = bookingSlice.actions;

export default bookingSlice.reducer;
